/**
 * Manages a responsive promotional message with a tooltip/dialog.
 * - Desktop: Behaves as a hover/focus tooltip, or a pinned popover when clicked.
 * - Mobile: Behaves as a bottom-sheet dialog.
 */

if (!customElements.get('promo-message')) {
  // ┌──────────────────────────────┐
  // │ Global State & Listeners     │
  // └──────────────────────────────┘
  // Shared across all instances to prevent O(n) memory leaks on collection pages

  let activePromoInstance = null;
  let globalListenersAttached = false;
  let resizeDebounceTimer = null;

  const handleGlobalClick = (e) => {
    if (!activePromoInstance) return;
    const { trigger, tooltip } = activePromoInstance;

    // If the click is outside both the trigger and the tooltip, close it
    if (!trigger.contains(e.target) && !tooltip.contains(e.target)) {
      activePromoInstance.isPinned = false;
      activePromoInstance.closeTooltip();
    }
  };

  const handleGlobalKeydown = (e) => {
    if (e.key === 'Escape' && activePromoInstance) {
      activePromoInstance.isPinned = false;
      activePromoInstance.closeTooltip();
      activePromoInstance.trigger.focus(); // Return focus for accessibility
    }
  };

  const handleGlobalResize = () => {
    // Debounce to avoid hammering getBoundingClientRect on every pixel of resize
    clearTimeout(resizeDebounceTimer);
    resizeDebounceTimer = setTimeout(() => {
      if (activePromoInstance) {
        activePromoInstance.handleResize();
      }
    }, 100);
  };

  const initGlobalListeners = () => {
    if (globalListenersAttached) return;
    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('touchstart', handleGlobalClick, { passive: true });
    document.addEventListener('keydown', handleGlobalKeydown);
    window.addEventListener('resize', handleGlobalResize);
    globalListenersAttached = true;
  };

  // ┌──────────────────────────────┐
  // │ Custom Element Definition    │
  // └──────────────────────────────┘

  customElements.define(
    'promo-message',
    class extends HTMLElement {
      connectedCallback() {
        this.container = this.querySelector('.product-promo-message');
        this.trigger = this.querySelector('.product-promo-info-icon');
        this.tooltip = this.querySelector('.product-promo-info-tooltip');
        this.closeBtn = this.querySelector('.promo-tooltip-close-mobile');

        if (!this.trigger || !this.tooltip || !this.container) return;

        // Defer appending tooltip to <body> until first open to avoid
        // ghost fixed/absolute elements polluting the DOM unnecessarily
        this.tooltipMounted = false;

        this.isOpen = false;
        this.isPinned = false;
        this.closeTimer = null;

        // Track which breakpoint context the tooltip was last opened in
        // so handleResize() can detect a crossing and respond correctly
        this.lastOpenedOnDesktop = null;

        // Start hidden so it has no layout impact before first open
        this.tooltip.style.display = 'none';

        // Ensure global listeners are attached (only runs once per page)
        initGlobalListeners();
        this.bindEvents();
      }

      disconnectedCallback() {
        // Clean up any pending close timer
        clearTimeout(this.closeTimer);

        // Clean up the detached DOM node if the host element is removed (e.g., via AJAX filtering)
        if (this.tooltip && this.tooltip.parentNode === document.body) {
          document.body.removeChild(this.tooltip);
        }

        // If this specific instance is destroyed while open, clear the global reference
        if (activePromoInstance === this) {
          activePromoInstance = null;
        }
      }

      bindEvents() {
        // ┌──────────────────────────────┐
        // │ Desktop Hover & Focus        │
        // └──────────────────────────────┘

        this.addEventListener('mouseenter', () => {
          if (window.innerWidth >= 768 && !this.isPinned) this.openTooltip();
        });
        this.addEventListener('mouseleave', () => {
          if (window.innerWidth >= 768 && !this.isPinned) this.closeTooltip();
        });

        this.tooltip.addEventListener('mouseenter', () => {
          if (window.innerWidth >= 768 && !this.isPinned) this.openTooltip();
        });
        this.tooltip.addEventListener('mouseleave', () => {
          if (window.innerWidth >= 768 && !this.isPinned) this.closeTooltip();
        });

        // Accessibility: Ensure keyboard navigation matches mouse hover behavior
        this.trigger.addEventListener('focus', () => {
          if (window.innerWidth >= 768) this.openTooltip();
        });
        this.trigger.addEventListener('blur', () => {
          if (window.innerWidth >= 768 && !this.isPinned) this.closeTooltip();
        });

        // ┌──────────────────────────────┐
        // │ Click Toggle                 │
        // └──────────────────────────────┘

        this.trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (window.innerWidth >= 768) {
            // On desktop, clicking toggles the "pinned" state, overriding hover behavior
            this.isPinned = !this.isPinned;
            this.isPinned ? this.openTooltip() : this.closeTooltip();
          } else {
            // On mobile, it acts as a standard toggle
            this.isOpen ? this.closeTooltip() : this.openTooltip();
          }
        });

        // ┌──────────────────────────────┐
        // │ Close Triggers               │
        // └──────────────────────────────┘

        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.isPinned = false;
            this.closeTooltip();
          });
        }
      }

      handleResize() {
        if (!this.isOpen || !this.tooltipMounted) return;

        const isDesktop = window.innerWidth >= 768;
        const crossedBreakpoint =
          this.lastOpenedOnDesktop !== null && this.lastOpenedOnDesktop !== isDesktop;

        if (crossedBreakpoint) {
          // Breakpoint boundary crossed while tooltip was open —
          // explicitly clear any lingering inline styles before closing
          this.tooltip.style.top = '';
          this.tooltip.style.left = '';
          this.tooltip.style.width = '';
          this.tooltip.classList.remove('align-left', 'align-right');

          this.isPinned = false;
          this.closeTooltip();
          return;
        }

        if (isDesktop) {
          this.positionDesktopTooltip();
        } else {
          // Strip inline styles and alignment classes so mobile CSS can take over
          this.tooltip.style.top = '';
          this.tooltip.style.left = '';
          this.tooltip.classList.remove('align-left', 'align-right');
        }
      }

      openTooltip() {
        // If another instance is currently open, close it first
        if (activePromoInstance && activePromoInstance !== this) {
          activePromoInstance.isPinned = false;
          activePromoInstance.closeTooltip();
        }

        // Lazily mount the tooltip to <body> on first open only
        if (!this.tooltipMounted) {
          document.body.appendChild(this.tooltip);
          this.tooltipMounted = true;
        }

        // Clear any pending close timer and restore display before animating in
        clearTimeout(this.closeTimer);
        this.tooltip.style.display = '';

        // Record which breakpoint context this was opened in
        this.lastOpenedOnDesktop = window.innerWidth >= 768;

        // Set this instance as the globally active one
        activePromoInstance = this;

        this.isOpen = true;
        this.tooltip.classList.add('is-active');

        // Update ARIA states for screen readers
        this.tooltip.setAttribute('aria-hidden', 'false');
        this.trigger.setAttribute('aria-expanded', 'true');

        if (window.innerWidth >= 768) {
          this.positionDesktopTooltip();
        } else {
          this.tooltip.style.top = '';
          this.tooltip.style.left = '';
        }
      }

      closeTooltip() {
        clearTimeout(this.closeTimer);

        this.isOpen = false;
        this.lastOpenedOnDesktop = null;
        this.tooltip.classList.remove('is-active');

        // Reset ARIA states
        this.tooltip.setAttribute('aria-hidden', 'true');
        this.trigger.setAttribute('aria-expanded', 'false');

        // After the CSS transition finishes, apply display:none so the browser
        // excludes the element from document height calculation entirely
        this.closeTimer = setTimeout(() => {
          if (!this.isOpen) {
            this.tooltip.style.display = 'none';
          }
        }, 300); // Matches the longest transition duration (0.3s)

        // Clear the global reference if this instance is the active one
        if (activePromoInstance === this) {
          activePromoInstance = null;
        }
      }

      positionDesktopTooltip() {
        const iconRect = this.trigger.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        const top = containerRect.bottom + window.scrollY + 12;
        const left = iconRect.left + window.scrollX + iconRect.width / 2;

        this.tooltip.style.top = `${top}px`;
        this.tooltip.style.left = `${left}px`;

        this.tooltip.classList.remove('align-left', 'align-right');
        if (iconRect.left + 160 > viewportWidth - 16) {
          this.tooltip.classList.add('align-right');
        } else if (iconRect.left - 160 < 16) {
          this.tooltip.classList.add('align-left');
        }
      }
    }
  );
}
